import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, Plus, Folder, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { listMyWorkspaces, listMyAccounts } from "@/lib/workspaces.functions";
import { WorkspaceWizard } from "@/components/workspace-wizard";
import { StuckImportAlert } from "@/components/stuck-import-alert";
import { RecentPushesWidget } from "@/components/recent-pushes-widget";
import { EmptyState } from "@/components/empty-state";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: Dashboard,
});

function Dashboard() {
  const { profile } = useAuth();
  const [wizardOpen, setWizardOpen] = useState(false);
  const fetchWs = useServerFn(listMyWorkspaces);
  const fetchAccts = useServerFn(listMyAccounts);
  const { data: wsData } = useQuery({ queryKey: ["my-workspaces"], queryFn: () => fetchWs() });
  const { data: acctData } = useQuery({ queryKey: ["my-accounts"], queryFn: () => fetchAccts() });
  const workspaces = wsData?.workspaces ?? [];
  const primaryAccount = acctData?.accounts?.[0];

  return (
    <div className="space-y-6">
      <StuckImportAlert />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {profile?.full_name ? `Welcome back, ${profile.full_name.split(" ")[0]}` : "Command Center"}
          </h1>
          <p className="text-sm text-muted-foreground">All your affiliate programs, in one view.</p>
        </div>
        <Button onClick={() => setWizardOpen(true)}><Plus className="h-4 w-4 mr-2" /> New workspace</Button>
      </div>

      {workspaces.length === 0 ? (
        <EmptyState
          icon={LayoutDashboard}
          title="Let's set up your first program"
          description="Drop a CSV from any affiliate dashboard. We handle the rest."
          action={<Button onClick={() => setWizardOpen(true)}><Plus className="h-4 w-4 mr-2" /> Create workspace</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {workspaces.map((w: any) => (
            <Link key={w.id} to="/workspaces/$workspaceId" params={{ workspaceId: w.id } as any}>
              <Card className="hover:border-primary transition-colors cursor-pointer">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Folder className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{w.program_name}</div>
                      <div className="text-xs text-muted-foreground capitalize">{w.revenue_model.replace("_", " ")}</div>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <RecentPushesWidget />

      {primaryAccount && <WorkspaceWizard open={wizardOpen} onOpenChange={setWizardOpen} accountId={primaryAccount.id} />}
    </div>
  );
}
